import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'expo-router';
import { AppDispatch } from '../redux/store';
import { selectGroceryToast, hideGroceryToast } from '../redux/uiSlice';
import { removeItemsByBatchId } from '../redux/groceryListSlice';
import { useTranslation } from 'react-i18next';

const TOAST_DURATION = 5000;

const GroceryToast: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const toast = useSelector(selectGroceryToast);
  const opacity = useRef(new Animated.Value(0)).current;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (toast) {
      Animated.timing(opacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();

      timerRef.current = setTimeout(() => {
        dismiss();
      }, TOAST_DURATION);
    } else {
      Animated.timing(opacity, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }).start();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [toast?.batchId]);

  const dismiss = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    Animated.timing(opacity, {
      toValue: 0,
      duration: 150,
      useNativeDriver: true,
    }).start(() => dispatch(hideGroceryToast()));
  };

  const handleUndo = () => {
    if (!toast) return;
    dispatch(removeItemsByBatchId(toast.batchId));
    dismiss();
  };

  const handleViewList = () => {
    router.navigate('/grocery-list' as any);
    dispatch(hideGroceryToast());
  };

  if (!toast) return null;

  const label = t('common.ingredientsAdded', { count: toast.itemCount });

  return (
    <Animated.View style={[styles.container, { opacity }]} pointerEvents="box-none">
      <View style={styles.toast} testID="grocery-toast">
        <Text style={styles.label} numberOfLines={1}>{label}</Text>
        <View style={styles.actions}>
          <TouchableOpacity onPress={handleUndo} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Text style={styles.undoText}>{t('common.undo')}</Text>
          </TouchableOpacity>
          <View style={styles.divider} />
          <TouchableOpacity onPress={handleViewList} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Text style={styles.viewText}>{t('common.viewList')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 90,
    left: 16,
    right: 16,
    zIndex: 9999,
  },
  toast: {
    backgroundColor: '#1b1c1a',
    borderRadius: 14,
    paddingVertical: 14,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    shadowColor: 'rgba(0, 0, 0, 0.24)',
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 12,
    elevation: 8,
  },
  label: {
    flex: 1,
    fontSize: 13,
    fontFamily: 'Inter-Medium',
    color: '#ffffff',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  divider: {
    width: 1,
    height: 14,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  undoText: {
    fontSize: 13,
    fontFamily: 'Inter-Medium',
    color: '#ffb68d',
  },
  viewText: {
    fontSize: 13,
    fontFamily: 'Inter-SemiBold',
    color: '#ffffff',
  },
});

export default GroceryToast;
